import styled from 'styled-components';

export const LoginWrapper = styled.section`
    font-family: ${({ theme }) => theme.fonts.inter} !important;
    width: 100vw;
    height: 100vh;
    margin: auto;
    position: relative;
    overflow: hidden;
    /* Figma gradient background (was white) */
    background:
        radial-gradient(1100px 700px at 82% 52%, rgba(227, 210, 246, 0.55) 0%, rgba(246, 239, 252, 0.25) 45%, rgba(255, 255, 255, 0) 78%),
        linear-gradient(135deg, #ffffff 0%, #faf6fe 48%, #efe4fa 100%);
    padding: 40px;

    @media (max-width: 991.99px) {
        padding: 30px;
    }

    /* Below md breakpoint - mobile/tablet view */
    @media (max-width: 768px) {
        padding: 24px;
        height: 100vh;
        display: flex;
        flex-direction: column;
    }

    @media (max-width: 480px) {
        padding: 16px;
    }

    @media (max-width: 375px) {
        padding: 12px;
    }

    .loginLogo {
        @media (max-width: 768px) {
            flex-shrink: 0;
            margin-top: 20px;
            margin-bottom: 20px;
        }

        @media (max-width: 480px) {
            margin-top: 16px;
            margin-bottom: 16px;
        }

        img {
            width: 136px;
            height: 32px;
            object-fit: contain;

            @media (max-width: 480px) {
                width: 120px;
                height: 28px;
            }
        }
    }

    /* Rectangular banner — kept for SetPassword (change-password-banner.png) */
    .loginBanner {
        width: 100%;
        height: 100%;
        position: relative;

        /* Hide banner on mobile/tablet */
        @media (max-width: 768px) {
            display: none;
        }

        img {
            border-radius: 16px;
            height: calc(100vh - 80px);
            min-height: auto;
            position: absolute;
            object-fit: cover;

            @media (max-width: 991.99px) {
                height: calc(100vh - 60px);
                border-radius: 14px;
            }
        }
    }

    /* Circular banner — Figma design. Used by Login + MagicLinkLanding + MagicLinkVerify + VerifyInvitation.
       Explicit height (like old .loginBanner img used calc(100vh - 80px)) so it gives the col
       a defined height and the circle stays truly vertically centered. */
    .loginBannerCircle {
        --banner-size: min(680px, calc(100vh - 80px), 80%);

        width: 100%;
        height: calc(100vh - 80px);
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        @media (max-width: 991.99px) {
            --banner-size: min(680px, calc(100vh - 60px), 80%);
            height: calc(100vh - 60px);
        }

        @media (max-width: 768px) {
            display: none;
        }

        .circle {
            position: relative;
            width: var(--banner-size);
            aspect-ratio: 1 / 1;
            overflow: hidden;

            img {
                width: 100%;
                height: 100%;
                object-fit: contain;
                display: block;
            }
        }
    }

    .loginContainer{
        width: 100%;
        position: relative;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        /* Mobile/tablet - center form vertically */
        @media (max-width: 768px) {
            position: static;
            top: auto;
            left: auto;
            transform: none;
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
        }

        h1{
            font-family: ${({ theme }) => theme.fonts.inter};
            /* Figma: SemiBold 24 #2C2926 (was 500 30 #000 in old) */
            font-weight: 600;
            font-size: 24px;
            line-height: 30px;
            color: #2C2926;
            margin: 0px 0px 20px 0px;

            @media (max-width: 991.99px) {
                font-size: 22px;
                line-height: 28px;
                margin: 0px 0px 18px 0px;
            }

            @media (max-width: 768px) {
                font-size: 22px;
                line-height: 28px;
                margin: 0px 0px 16px 0px;
            }

            @media (max-width: 480px) {
                font-size: 20px;
                line-height: 26px;
                margin: 0px 0px 14px 0px;
            }

            @media (max-width: 375px) {
                font-size: 19px;
                line-height: 24px;
                margin: 0px 0px 12px 0px;
            }
        }

        p{
            font-family: ${({ theme }) => theme.fonts.inter};
            /* Figma: Medium 16 #2C2926 (was 400 14 #000 in old).
               Progressively smaller at narrow viewports so the "Welcome back
               [email]. ← Change email" line fits on one line as much as possible. */
            font-weight: 500;
            font-size: 16px;
            line-height: 22px;
            color: #2C2926;
            margin-bottom: 30px;

            @media (max-width: 1199.99px) {
                font-size: 14px;
                line-height: 20px;
            }

            @media (max-width: 991.99px) {
                font-size: 13px;
                line-height: 19px;
            }

            @media (max-width: 768px) {
                font-size: 14px;
                line-height: 20px;
                margin-bottom: 24px;
            }

            @media (max-width: 480px) {
                font-size: 13px;
                line-height: 19px;
                margin-bottom: 20px;
            }

            @media (max-width: 375px) {
                font-size: 12px;
                line-height: 18px;
                margin-bottom: 16px;
            }

            a{
                font-weight: 500;
                color: #2E45DA;
                white-space: nowrap;
            }

            strong {
                font-weight: 600;
                word-break: break-word;
            }

            /* Keep the email + back link as a single group — they wrap together
               to the next line when the container isn't wide enough for the
               full subtitle on one line. */
            .emailLinkGroup {
                display: inline-block;
                white-space: nowrap;

                /* At the narrowest viewports, allow the group to wrap internally
                   if even the group alone exceeds container width. */
                @media (max-width: 480px) {
                    white-space: normal;
                }
            }
        }

        .loginBtn {
            /* Figma: solid #2E45DA rounded-12, 52h, white SemiBold 16 */
            background: #2E45DA !important;
            border: 1px solid #2E45DA !important;
            border-radius: 12px !important;
            color: #ffffff !important;
            font-family: ${({ theme }) => theme.fonts.inter} !important;
            font-weight: 600 !important;
            font-size: 16px !important;
            height: 52px !important;
            box-shadow:
                0px 6px 29px 0px rgba(89, 108, 148, 0.07),
                0px 0.751px 3.631px 0px rgba(89, 108, 148, 0.04) !important;

            &:hover:not(:disabled) {
                background: #2e1f52 !important;
                border-color: #2e1f52 !important;
                color: #ffffff !important;
            }

            @media (max-width: 768px) {
                height: 48px !important;
                font-size: 15px !important;
            }

            @media (max-width: 480px) {
                height: 46px !important;
                font-size: 14px !important;
            }

            @media (max-width: 375px) {
                height: 44px !important;
                font-size: 14px !important;
            }
        }
    }

    /* Figma form input styling — scoped to forms with loginFormV2 class (Login only).
       SetPassword's underlined variant stays untouched. */
    .loginFormV2 {
        .ant-form-item-label > label {
            font-family: ${({ theme }) => theme.fonts.inter};
            font-weight: 500;
            font-size: 14px;
            color: #2C2926;
            letter-spacing: 0.14px;
        }

        .ant-form-item-label > label::after {
            display: none;
        }

        .ant-form-item-label > label.ant-form-item-required::before {
            display: none;
        }

        .ant-input,
        .ant-input-password,
        .ant-input-affix-wrapper {
            height: 48px;
            border-radius: 12px;
            border: 1px solid #d4d7e3;
            background: #ffffff;
            padding: 0 16px;
            font-family: ${({ theme }) => theme.fonts.inter};
            font-weight: 500;
            font-size: 16px;
            color: #2C2926;
            box-shadow: none;
        }

        .ant-input-affix-wrapper .ant-input {
            height: auto;
            border: none;
            padding: 0;
            background: transparent;
        }

        .ant-input:hover,
        .ant-input-affix-wrapper:hover {
            border-color: #b4a3d8;
        }

        .ant-input:focus,
        .ant-input-focused,
        .ant-input-affix-wrapper-focused {
            border-color: #2E45DA;
            box-shadow: 0 0 0 3px rgba(46, 69, 218, 0.08);
        }

        .ant-input::placeholder,
        .ant-input-affix-wrapper input::placeholder {
            color: #8897ad;
            font-weight: 500;
        }

        .ant-input[disabled],
        .ant-input-affix-wrapper-disabled {
            background: #f7f8fc !important;
            color: #2C2926;
        }
    }

    .forgotPassword{
        font-family: ${({ theme }) => theme.fonts.inter};
        font-weight: 400;
        font-size: 14px;
        line-height: 19px;
        color: #000;
        text-decoration: none;

        @media (max-width: 768px) {
            font-size: 14px;
        }

        @media (max-width: 480px) {
            font-size: 13px;
        }

        &:hover{
            color: ${({ theme }) => theme.settings.primary};
        }
    }

    /* Override browser autofill background color */
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 1000px white inset !important;
        -webkit-text-fill-color: #2C2926 !important;
        transition: background-color 5000s ease-in-out 0s;
    }

    /* Responsive form inputs */
    .ant-input,
    .ant-input-password {
        @media (max-width: 768px) {
            font-size: 15px;
        }

        @media (max-width: 480px) {
            font-size: 14px;
        }
    }

    .ant-form-item-label > label {
        @media (max-width: 768px) {
            font-size: 14px;
        }

        @media (max-width: 480px) {
            font-size: 13px;
        }
    }

    /* Row and column adjustments for mobile */
    .ant-row {
        @media (max-width: 768px) {
            margin-left: 0 !important;
            margin-right: 0 !important;
            flex: 1;
            display: flex;
            flex-direction: column;
        }
    }

    .ant-col {
        @media (max-width: 768px) {
            padding-left: 0 !important;
            padding-right: 0 !important;
            flex: none !important;
            max-width: 100% !important;
            width: 100% !important;
        }

        &:first-child {
            @media (max-width: 768px) {
                flex: 1 !important;
                display: flex;
                flex-direction: column;
            }
        }
    }
`;
